import React, { useMemo } from 'react'
import {
  Box, Button, TextField, Paper, FormControl, InputLabel, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Typography, Alert
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import { router, usePage, useForm } from '@inertiajs/react'
import AppShell from '../../../../components/layout/AppShell'
import PageHeader from '../../../../components/ui/PageHeader'
import FormattedCurrency from '../../../../components/ui/FormattedCurrency'

New.layout = (page) => <AppShell>{page}</AppShell>

const blankLine = () => ({ account_id: '', debit_amount: '', credit_amount: '', description: '' })

const toNumber = (value) => {
  if (value === '' || value == null) return 0
  const parsed = parseFloat(String(value).replace(',', '.'))
  return Number.isFinite(parsed) ? parsed : 0
}

export default function New({ property, fiscal_year, fiscal_years = [], accounts = [], form, errors: serverErrors = {} }) {
  const { props } = usePage()
  const tenant = props.tenant || {}
  const basePath = `/m/${tenant.slug}/properties/${property.id}`

  const initialLines = (form?.lines && form.lines.length > 0) ? form.lines : [ blankLine(), blankLine() ]

  const { data, setData, post, processing, errors } = useForm({
    fiscal_year_id: form?.fiscal_year_id || fiscal_year.id,
    entry_date: form?.entry_date || fiscal_year.start_date || '',
    description: form?.description || '',
    lines: initialLines,
  })

  const allErrors = { ...serverErrors, ...errors }

  const totals = useMemo(() => {
    const debit = data.lines.reduce((sum, l) => sum + toNumber(l.debit_amount), 0)
    const credit = data.lines.reduce((sum, l) => sum + toNumber(l.credit_amount), 0)
    return { debit, credit, balanced: Math.abs(debit - credit) < 0.005 && debit > 0 }
  }, [data.lines])

  const setLineField = (index, field, value) => {
    const next = data.lines.map((l, i) => (i === index ? { ...l, [field]: value } : l))
    setData('lines', next)
  }

  const addLine = () => setData('lines', [ ...data.lines, blankLine() ])

  const removeLine = (index) => {
    if (data.lines.length <= 2) return
    setData('lines', data.lines.filter((_, i) => i !== index))
  }

  const changeFiscalYear = (id) => {
    router.get(`${basePath}/journal/new`, { fiscal_year_id: id }, { preserveState: false })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    post(`${basePath}/journal`)
  }

  const baseError = allErrors.base
  const baseErrorMessages = Array.isArray(baseError) ? baseError : (baseError ? [ baseError ] : [])

  return (
    <Box>
      <PageHeader
        title="Buchung erfassen"
        breadcrumbs={[
          { label: property.name, href: basePath },
          { label: 'Journal', href: `${basePath}/journal` },
          { label: 'Neu' },
        ]}
      />

      {baseErrorMessages.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="base-error">
          {baseErrorMessages.map((m, i) => <div key={i}>{m}</div>)}
        </Alert>
      )}

      <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
        <Box component="form" onSubmit={handleSubmit} data-testid="journal-form">
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 160 }} required>
              <InputLabel>Geschäftsjahr</InputLabel>
              <Select value={data.fiscal_year_id} label="Geschäftsjahr"
                onChange={(e) => changeFiscalYear(e.target.value)}
                data-testid="fiscal-year-select">
                {fiscal_years.map((fy) => (
                  <MenuItem key={fy.id} value={fy.id}>{fy.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Buchungsdatum" required size="small" type="date"
              value={data.entry_date} onChange={(e) => setData('entry_date', e.target.value)}
              error={!!allErrors.entry_date} helperText={allErrors.entry_date?.[0]}
              slotProps={{ inputLabel: { shrink: true } }}
              data-testid="entry-date-input" />
            <TextField label="Beschreibung" required size="small" sx={{ flex: 1, minWidth: 240 }}
              value={data.description} onChange={(e) => setData('description', e.target.value)}
              error={!!allErrors.description} helperText={allErrors.description?.[0]}
              data-testid="description-input" />
          </Box>

          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
                  <TableCell sx={{ fontWeight: 600, width: '40%' }}>Konto</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Soll</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Haben</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Beschreibung</TableCell>
                  <TableCell sx={{ width: 48 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.lines.map((line, idx) => {
                  const lineError = allErrors[`lines.${idx}.amount`] || allErrors[`lines.${idx}.account_id`]
                  return (
                    <TableRow key={idx} data-testid={`line-row-${idx}`}>
                      <TableCell>
                        <FormControl fullWidth size="small" error={!!allErrors[`lines.${idx}.account_id`]}>
                          <Select value={line.account_id || ''}
                            onChange={(e) => setLineField(idx, 'account_id', e.target.value)}
                            displayEmpty
                            data-testid={`line-${idx}-account`}>
                            <MenuItem value=""><em>— Konto wählen —</em></MenuItem>
                            {accounts.map((a) => (
                              <MenuItem key={a.id} value={a.id}>{a.number} — {a.name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell align="right">
                        <TextField size="small" type="number"
                          value={line.debit_amount}
                          onChange={(e) => setLineField(idx, 'debit_amount', e.target.value)}
                          slotProps={{ htmlInput: { step: '0.01', min: '0', style: { textAlign: 'right' } } }}
                          data-testid={`line-${idx}-debit`} />
                      </TableCell>
                      <TableCell align="right">
                        <TextField size="small" type="number"
                          value={line.credit_amount}
                          onChange={(e) => setLineField(idx, 'credit_amount', e.target.value)}
                          slotProps={{ htmlInput: { step: '0.01', min: '0', style: { textAlign: 'right' } } }}
                          data-testid={`line-${idx}-credit`} />
                      </TableCell>
                      <TableCell>
                        <TextField size="small" fullWidth
                          value={line.description}
                          onChange={(e) => setLineField(idx, 'description', e.target.value)}
                          data-testid={`line-${idx}-description`} />
                        {lineError && (
                          <Typography variant="caption" color="error" data-testid={`line-${idx}-error`}>
                            {Array.isArray(lineError) ? lineError[0] : lineError}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => removeLine(idx)}
                          disabled={data.lines.length <= 2}
                          aria-label="Zeile entfernen"
                          data-testid={`line-${idx}-remove`}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )
                })}
                <TableRow sx={{ backgroundColor: '#F9FAFB' }} data-testid="totals-row">
                  <TableCell sx={{ fontWeight: 600 }}>Summe</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }} data-testid="total-debit">
                    <FormattedCurrency value={totals.debit} />
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }} data-testid="total-credit">
                    <FormattedCurrency value={totals.credit} />
                  </TableCell>
                  <TableCell colSpan={2}>
                    {totals.balanced ? (
                      <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }} data-testid="balance-status">
                        Ausgeglichen
                      </Typography>
                    ) : (
                      <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 600 }} data-testid="balance-status">
                        Differenz: <FormattedCurrency value={totals.debit - totals.credit} />
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Button startIcon={<AddIcon />} onClick={addLine} data-testid="add-line-btn">
              Zeile hinzufügen
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={() => router.visit(`${basePath}/journal`)} data-testid="cancel-btn">
              Abbrechen
            </Button>
            <Button type="submit" variant="contained"
              disabled={processing || !totals.balanced}
              data-testid="submit-btn">
              {processing ? 'Wird gebucht...' : 'Buchen'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}
